/**
 * AI Assistant for App Builder – Describe your app and get layout/panel suggestions.
 * Uses LLM API when available; falls back to keyword-based inference.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { pythonClient } from '../../api/axios.client';

const SUGGESTION_KEYWORDS = {
  crm: { layout: 'three-panel', panels: [{ type: 'table', position: 'left' }, { type: 'chat', position: 'center' }, { type: 'stats', position: 'right' }] },
  dashboard: { layout: 'grid', panels: [{ type: 'stats', position: 'top' }, { type: 'chart', position: 'left' }, { type: 'table', position: 'right' }] },
  lead: { layout: 'three-panel', panels: [{ type: 'form', position: 'left' }, { type: 'chat', position: 'center' }, { type: 'table', position: 'right' }] },
  faq: { layout: 'two-panel', panels: [{ type: 'chat', position: 'left' }, { type: 'rich_text', position: 'right' }] },
  support: { layout: 'three-panel', panels: [{ type: 'list', position: 'left' }, { type: 'chat', position: 'center' }, { type: 'form', position: 'right' }] },
  analytics: { layout: 'grid', panels: [{ type: 'stats', position: 'top' }, { type: 'chart', position: 'left' }, { type: 'chart', position: 'right' }] },
};

function inferSuggestionFallback(description) {
  const lower = (description || '').toLowerCase();
  for (const [keyword, config] of Object.entries(SUGGESTION_KEYWORDS)) {
    if (lower.includes(keyword)) return config;
  }
  return { layout: 'three-panel', panels: [{ type: 'table', position: 'left' }, { type: 'chat', position: 'center' }, { type: 'summary', position: 'right' }] };
}

export default function AppBuilderAIAssistant({ open, onClose, onApply, existingPanels = [] }) {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    setSuggestion(null);
    setError(null);
    try {
      const res = await pythonClient.post('/app-builder/generate-layout', { description });
      if (res?.layout && Array.isArray(res?.panels)) {
        setSuggestion({ layout: res.layout, panels: res.panels });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'AI generation failed');
      setSuggestion(inferSuggestionFallback(description));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (suggestion && onApply) {
      onApply(suggestion);
      onClose();
      setDescription('');
      setSuggestion(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyIcon color="primary" />
        AI App Assistant
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe your app and we&apos;ll suggest a layout and panels.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="e.g. A CRM dashboard with lead table, chat for qualification, and stats on the right"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button variant="outlined" onClick={handleGenerate} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SmartToyIcon />}>
          {loading ? 'Generating...' : 'Suggest layout'}
        </Button>
        {error && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setError(null)}>
            AI unavailable, using keyword match. {error}
          </Alert>
        )}
        {suggestion && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Suggested layout</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
              <Chip label={suggestion.layout} size="small" color="primary" variant="outlined" />
              {suggestion.panels?.map((p, i) => (
                <Chip key={i} label={`${p.type} (${p.position})`} size="small" variant="outlined" />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Click Apply to add these panels to your app.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={!suggestion}>
          Apply suggestion
        </Button>
      </DialogActions>
    </Dialog>
  );
}
